import { useEffect, useState } from "react";
import { Wallet, ShieldCheck, Trash2, Loader2, ExternalLink, CheckCircle2, Banknote, BellRing, AlertTriangle, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { DashboardLayout } from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { api, errorMessage } from "../lib/api";
import { useLang } from "../context/LanguageContext";

const HELP_STEPS = [
  "Logg inn på portal.vippsmobilepay.com med bedriftskontoen din",
  "Gå til «Utvikler» / «Developer» og velg salgsenheten (MSN) du vil bruke",
  "Kopier client_id, client_secret og Ocp-Apim-Subscription-Key (subscription key)",
  "Lim inn nøklene her og trykk «Test og lagre»",
];

export default function PaymentSettingsPage() {
  const { t } = useLang();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [form, setForm] = useState({ client_id: "", client_secret: "", subscription_key: "", msn: "", env: "production" });

  const load = () => {
    api
      .get("/my/vipps")
      .then((res) => setStatus(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.client_id.trim() || !form.client_secret.trim() || !form.subscription_key.trim() || !form.msn.trim()) {
      toast.error(t("Fyll ut alle feltene"));
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/my/vipps", {
        client_id: form.client_id.trim(),
        client_secret: form.client_secret.trim(),
        subscription_key: form.subscription_key.trim(),
        msn: form.msn.trim(),
        env: form.env,
      });
      toast.success(t(data.message));
      setForm({ client_id: "", client_secret: "", subscription_key: "", msn: "", env: form.env });
      load();
    } catch (err) {
      toast.error(errorMessage(err, t("Kunne ikke lagre Vipps-oppsettet")));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setRemoving(true);
    try {
      const { data } = await api.delete("/my/vipps");
      toast.success(t(data.message));
      load();
    } catch (err) {
      toast.error(errorMessage(err, t("Kunne ikke fjerne oppsettet")));
    } finally {
      setRemoving(false);
    }
  };

  const [reregistering, setReregistering] = useState(false);
  const reregisterWebhook = async () => {
    setReregistering(true);
    try {
      const { data } = await api.post("/my/vipps/webhook");
      toast.success(t(data.message));
      load();
    } catch (err) {
      toast.error(errorMessage(err, t("Kunne ikke registrere varsling fra Vipps")));
      load();
    } finally {
      setReregistering(false);
    }
  };

  const webhook = status?.webhook || { status: "missing" };
  const webhookLabel = {
    active: t("Varsling fra Vipps: aktiv"),
    error: t("Varsling fra Vipps: feilet"),
    skipped: t("Varsling fra Vipps: ikke tilgjengelig her"),
    missing: t("Varsling fra Vipps: ikke registrert"),
  }[webhook.status] || t("Varsling fra Vipps: ikke registrert");

  return (
    <DashboardLayout>
      <div className="max-w-3xl space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight flex items-center gap-2.5" data-testid="vipps-page-title">
            <Wallet className="w-6 h-6" />
            {t("Betaling med Vipps")}
          </h1>
          <p className="text-muted-foreground mt-2">
            {t("Koble til ditt eget Vipps MobilePay-oppsett, så går betalingene rett til din konto.")}
          </p>
        </div>

        {loading ? (
          <Skeleton className="h-24 rounded-xl" />
        ) : (
          <Card className="border-border" data-testid="vipps-status-card">
            <CardContent className="p-5 space-y-3">
              <div className="flex flex-wrap items-center gap-3">
              {status?.configured ? (
                <>
                  <Badge className="bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] hover:bg-[hsl(var(--success))]" data-testid="vipps-status-active">
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                    {t("Vipps aktiv")}
                  </Badge>
                  <span className="text-sm text-muted-foreground" data-testid="vipps-status-details">
                    MSN {status.msn} · client_id {status.client_id_masked} · {status.env === "production" ? t("Produksjon") : status.env === "simulated" ? t("Simulert (demo – ingen ekte betalinger)") : t("Testmiljø")}
                  </span>
                  <span className="ml-auto">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" disabled={removing} data-testid="vipps-remove-button">
                          <Trash2 className="w-4 h-4 mr-1.5" />
                          {t("Fjern oppsett")}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t("Fjerne Vipps-oppsettet?")}</AlertDialogTitle>
                          <AlertDialogDescription>
                            {t("Betalte billetter kan ikke selges før et nytt oppsett er lagret. Gratisbilletter påvirkes ikke.")}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="vipps-remove-cancel">{t("Avbryt")}</AlertDialogCancel>
                          <AlertDialogAction onClick={remove} data-testid="vipps-remove-confirm">{t("Ja, fjern")}</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </span>
                </>
              ) : (
                <>
                  <Badge variant="secondary" data-testid="vipps-status-inactive">{t("Ikke konfigurert")}</Badge>
                  <span className="text-sm text-muted-foreground">
                    {t("Legg inn nøklene dine under for å aktivere betalte billetter.")}
                  </span>
                </>
              )}
              </div>
              {status?.configured && (
                <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-border" data-testid="vipps-webhook-row">
                  {webhook.status === "active" ? (
                    <Badge className="bg-[hsl(var(--success))]/15 text-foreground border border-[hsl(var(--success))]/50 hover:bg-[hsl(var(--success))]/15 font-normal" data-testid="vipps-webhook-status">
                      <BellRing className="w-3 h-3 mr-1" />{webhookLabel}
                    </Badge>
                  ) : webhook.status === "error" ? (
                    <Badge className="bg-[hsl(var(--warning))]/20 text-foreground border border-[hsl(var(--warning))]/60 hover:bg-[hsl(var(--warning))]/20 font-normal" data-testid="vipps-webhook-status">
                      <AlertTriangle className="w-3 h-3 mr-1" />{webhookLabel}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="font-normal text-muted-foreground" data-testid="vipps-webhook-status">{webhookLabel}</Badge>
                  )}
                  <span className="text-xs text-muted-foreground" data-testid="vipps-webhook-help">
                    {webhook.status === "active"
                      ? t("Vipps varsler Right in straks en betaling er godkjent, avbrutt eller refundert. Betalinger bekreftes uansett også ved statussjekk.")
                      : webhook.status === "error"
                        ? (webhook.error || t("Kunne ikke registreres hos Vipps. Betalinger bekreftes via statussjekk inntil videre."))
                        : t("Betalinger bekreftes via statussjekk.")}
                  </span>
                  {webhook.status !== "active" && webhook.status !== "skipped" && (
                    <Button variant="outline" size="sm" className="ml-auto" onClick={reregisterWebhook} disabled={reregistering} data-testid="vipps-webhook-reregister-button">
                      {reregistering ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <BellRing className="w-4 h-4 mr-1.5" />}
                      {t("Registrer varsling på nytt")}
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Card className="border-border" data-testid="vipps-money-flow-card">
          <CardContent className="p-6">
            <h2 className="font-heading font-semibold mb-4 flex items-center gap-2">
              <Banknote className="w-5 h-5" />
              {t("Pengene går rett til din konto")}
            </h2>
            <ul className="space-y-3 text-sm">
              <li className="flex gap-3">
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-[hsl(var(--success))]" />
                <span>
                  {t("Når publikum betaler med Vipps, gjøres oppgjøret")} <strong>{t("direkte til bankkontoen som er registrert på din egen Vipps-avtale")}</strong> {t("(salgsenheten/MSN). Kontonummeret styrer du selv i Vipps-portalen.")}
                </span>
              </li>
              <li className="flex gap-3">
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-[hsl(var(--success))]" />
                <span>
                  <strong>{t("Right in er aldri mellomledd for pengene dine")}</strong> {t("– vi verken mottar eller holder på billettinntektene.")}
                </span>
              </li>
              <li className="flex gap-3">
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-[hsl(var(--success))]" />
                <span>
                  {t("Vårt gebyr (2,9 % per solgte billett, minst 99 kr per gjennomførte arrangement) faktureres separat i etterkant – se")} <span className="whitespace-nowrap">«{t("Fakturaer")}»</span> {t("i dashbordet.")}
                </span>
              </li>
              <li className="flex gap-3" data-testid="vipps-refund-info">
                <Undo2 className="w-4 h-4 mt-0.5 shrink-0 text-[hsl(var(--success))]" />
                <span>
                  <strong>{t("Refusjon gjør du selv")}</strong> {t("under Deltakere → Kjøpere («Refunder»). Hele bestillingen refunderes fra din Vipps-konto til kjøperen, billettene blir ugyldige og plassene ledige igjen. Ubetalte Vipps-bestillinger kanselleres automatisk etter 15 minutter.")}
                </span>
              </li>
            </ul>
            <p className="text-xs text-muted-foreground mt-4">
              {t("Forutsetning: en Vipps MobilePay-bedriftsavtale på organisasjonen din (krever org.nr. og bankkonto).")}{" "}
              {t("Har du ikke avtale ennå, oppretter du den i Vipps-portalen – lenke nederst på siden.")}
            </p>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="p-6">
            <h2 className="font-heading font-semibold mb-1">
              {status?.configured ? t("Oppdater nøklene") : t("Legg inn Vipps-nøklene dine")}
            </h2>
            <p className="text-sm text-muted-foreground mb-5">
              {t("Nøklene testes mot Vipps før de lagres, så du vet at alt virker. De vises aldri i klartekst etterpå.")}
            </p>
            <form onSubmit={save} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="vipps-msn">MSN (Merchant Serial Number)</Label>
                  <Input id="vipps-msn" value={form.msn} onChange={set("msn")} placeholder="123456" maxLength={20} data-testid="vipps-msn-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="vipps-env">{t("Miljø")}</Label>
                  <Select value={form.env} onValueChange={(v) => setForm((f) => ({ ...f, env: v }))}>
                    <SelectTrigger id="vipps-env" data-testid="vipps-env-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="production" data-testid="vipps-env-production">{t("Produksjon (ekte betalinger)")}</SelectItem>
                      <SelectItem value="test" data-testid="vipps-env-test">{t("Test (apitest.vipps.no)")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="vipps-client-id">Client ID</Label>
                <Input id="vipps-client-id" value={form.client_id} onChange={set("client_id")} autoComplete="off" data-testid="vipps-client-id-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="vipps-client-secret">Client Secret</Label>
                <Input id="vipps-client-secret" type="password" value={form.client_secret} onChange={set("client_secret")} autoComplete="new-password" data-testid="vipps-client-secret-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="vipps-subscription-key">Subscription Key (Ocp-Apim)</Label>
                <Input id="vipps-subscription-key" type="password" value={form.subscription_key} onChange={set("subscription_key")} autoComplete="new-password" data-testid="vipps-subscription-key-input" />
              </div>
              <Button type="submit" disabled={saving} data-testid="vipps-save-button">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    {t("Tester mot Vipps...")}
                  </>
                ) : (
                  <>
                    <ShieldCheck className="w-4 h-4 mr-1.5" />
                    {t("Test og lagre")}
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="border-border bg-secondary/40">
          <CardContent className="p-6">
            <h2 className="font-heading font-semibold mb-3">{t("Slik finner du nøklene")}</h2>
            <ol className="space-y-2 text-sm text-muted-foreground list-decimal pl-5" data-testid="vipps-help-steps">
              {HELP_STEPS.map((step) => (
                <li key={step}>{t(step)}</li>
              ))}
            </ol>
            <a
              href="https://portal.vippsmobilepay.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm underline underline-offset-4 mt-4"
              data-testid="vipps-portal-link"
            >
              {t("Åpne Vipps MobilePay-portalen")}
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
